import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useAudioRecorder } from "./useAudioRecorder";
import { useVoiceToTodo } from "./useVoiceToTodo";
import type { ProposedTodo } from "./useVoiceToTodo";
import { saveReviewedTodos } from "../lib/saveReviewedTodos";
import { useDialog } from "../contexts/DialogContext";
import type { List, Tag, NewSubtaskFields, NewTask, SubTask } from "../types";

interface UseVoiceIntegrationArgs {
  lists: List[];
  tags: Tag[];
  addTask: (task: Omit<NewTask, "links" | "pending_files">) => Promise<unknown>;
  addSubtask?: (
    taskId: string,
    title: string,
    fields?: NewSubtaskFields,
  ) => Promise<SubTask | null>;
  setSubtaskTags?: (subtaskId: string, tagIds: string[]) => Promise<boolean>;
}

export interface UseVoiceIntegrationResult {
  isRecording: boolean;
  recordingDuration: number;
  analyserNode: AnalyserNode | null;
  startRecording: () => void;
  stopRecording: () => void;
  isProcessingVoice: boolean;
  transcript: string | null;
  proposedTodos: ProposedTodo[];
  showVoiceNoResults: boolean;
  handleAcceptVoiceTodos: (todos: ProposedTodo[]) => Promise<void>;
  clearVoiceResults: () => void;
}

export function useVoiceIntegration({
  lists,
  tags,
  addTask,
  addSubtask,
  setSubtaskTags,
}: UseVoiceIntegrationArgs): UseVoiceIntegrationResult {
  const dialog = useDialog();

  const {
    isRecording,
    duration: recordingDuration,
    audioBlob,
    error: recordingError,
    analyserNode,
    startRecording,
    stopRecording,
    clearRecording,
  } = useAudioRecorder();

  const {
    isProcessing: isProcessingVoice,
    transcript,
    proposedTodos,
    error: voiceError,
    processAudio,
    clearResults: clearVoiceResults,
  } = useVoiceToTodo();

  const [showVoiceNoResults, setShowVoiceNoResults] = useState(false);

  // Process audio when recording stops
  useEffect(() => {
    if (!audioBlob || isRecording) return;
    processAudio(audioBlob, lists, tags).then((hasResults) => {
      if (hasResults) {
        dialog.open("voice-review");
      } else {
        setShowVoiceNoResults(true);
        setTimeout(() => setShowVoiceNoResults(false), 3000);
      }
      clearRecording();
    });
  }, [audioBlob, isRecording, lists, tags, processAudio, clearRecording, dialog]);

  // Surface recording and processing errors as toasts
  useEffect(() => {
    if (recordingError) {
      toast.error(`Recording error: ${recordingError}`);
    }
    if (voiceError) {
      toast.error(`Voice processing error: ${voiceError}`);
    }
  }, [recordingError, voiceError]);

  const handleAcceptVoiceTodos = async (todos: ProposedTodo[]) => {
    await saveReviewedTodos(todos, {
      addTask,
      addSubtask:
        addSubtask ??
        (async () => {
          throw new Error("Failed to create subtask from review");
        }),
      setSubtaskTags: setSubtaskTags ?? (async () => false),
    });
    dialog.close();
    clearVoiceResults();
  };

  return {
    isRecording,
    recordingDuration,
    analyserNode,
    startRecording,
    stopRecording,
    isProcessingVoice,
    transcript,
    proposedTodos,
    showVoiceNoResults,
    handleAcceptVoiceTodos,
    clearVoiceResults,
  };
}
